import React, { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import { collection, getDocs, doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import "../../App.css";
import Navbar from "../../componenets/Navbar";  
import Footer from "../../componenets/Footer";

interface UserProfile {
  uid: string;
  fullName: string;
  email: string;
  role: string;
  status: string;
  paymentStatus: "paid" | "unpaid" | "exempt";
  licenseNumber?: string;
}

// Added interface for multiple rates
interface ExchangeRates {
  [key: string]: number;
}

const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'JPY', 'CNY', 'LKR', 'INR', 'AUD', 'CAD'];

const AdminDashboard = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  // Changed usdRate to a rates object
  const [rates, setRates] = useState<ExchangeRates>({});
  const [isUpdatingRate, setIsUpdatingRate] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const userList = querySnapshot.docs.map(doc => doc.data() as UserProfile);
      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const rateDoc = await getDoc(doc(db, "settings", "exchangeRates"));
      const initialRates: ExchangeRates = {};
      if (rateDoc.exists()) {
        const data = rateDoc.data();
        SUPPORTED_CURRENCIES.forEach(curr => {
          initialRates[curr] = data[curr] || 0;
        });
      } else {
        SUPPORTED_CURRENCIES.forEach(curr => { initialRates[curr] = 0; });
      }
      setRates(initialRates);
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchExchangeRate()]);
      setLoading(false);
    };
    loadAllData();
  }, []);

  // Dashboard Stats
  const totalClients = users.filter(u => u.role === 'client').length;
  const totalAgents = users.filter(u => u.role === 'agent').length;
  const pendingAgents = users.filter(u => u.role === 'agent' && u.status === 'pending').length;
  const unpaidUsers = users.filter(u => u.paymentStatus === 'unpaid').length;

  const handleApprove = async (uid: string) => {
    try {
      await updateDoc(doc(db, "users", uid), { status: "approved" });
      alert("Agent Approved!");
      fetchUsers();
    } catch (error) { alert("Approval failed"); }
  };

  const handleReject = async (uid: string) => {
    if (window.confirm("Are you sure you want to reject this agent? This will prevent them from listing services.")) {
      try {
        await updateDoc(doc(db, "users", uid), { status: "rejected" });
        alert("Agent Rejected.");
        fetchUsers();
      } catch (error) { alert("Rejection failed"); }
    }
  };

  const handleMarkAsPaid = async (uid: string) => {
    try {
      await updateDoc(doc(db, "users", uid), { paymentStatus: "paid" });
      alert("Payment status updated to PAID.");
      fetchUsers();
    } catch (error) { alert("Update failed"); }
  };

  // --- NEW HANDLERS FOR MULTI-CURRENCY ---
  const handleUpdateRate = async () => {
    setIsUpdatingRate(true);
    try {
      await setDoc(doc(db, "settings", "exchangeRates"), { 
        ...rates, 
        lastUpdated: new Date().toISOString() 
      }, { merge: true });
      alert("All rates updated successfully!");
    } catch (error) { alert("Failed to update rates."); }
    finally { setIsUpdatingRate(false); }
  };

  const syncWithLiveApi = async () => {
    setIsUpdatingRate(true);
    try {
      const response = await fetch("https://open.er-api.com/v6/latest/LKR");
      const data = await response.json();
      if (data.result === "success") {
        const liveRates: ExchangeRates = {};
        SUPPORTED_CURRENCIES.forEach(curr => {
          const rateToLkr = 1 / data.rates[curr];
          liveRates[curr] = parseFloat(rateToLkr.toFixed(2));
        });
        setRates(liveRates);
        alert("Live rates fetched! Review and click Save.");
      }
    } catch (error) { alert("API fetch failed."); }
    finally { setIsUpdatingRate(false); }
  };

  const clearAllRates = () => {
    if (window.confirm("Clear all rate inputs?")) {
      const reset: ExchangeRates = {};
      SUPPORTED_CURRENCIES.forEach(curr => reset[curr] = 0);
      setRates(reset);
    }
  };

  if (loading) return <div className="app-status">Initializing Admin...</div>;

  return (
    <div className="admin-page-wrapper">
      <Navbar />
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <div className="sidebar-header"><h3>Admin Panel</h3></div>
          <nav className="sidebar-nav">
            <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>📊 Dashboard Overview</button>
            <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>👥 User Management</button>
            <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>⚙️ Global Settings</button>
          </nav>
          <div className="sidebar-stats">
            <div className="stat-item"><span>Total Clients</span><strong>{totalClients}</strong></div>
            <div className="stat-item"><span>Total Agents</span><strong>{totalAgents}</strong></div>
            <div className="stat-item pending"><span>Pending Agents</span><strong>{pendingAgents}</strong></div>
            <div className="stat-item warning"><span>Unpaid Users</span><strong>{unpaidUsers}</strong></div>
          </div>
        </aside>

        <main className="admin-main">
          {activeTab === 'overview' && (
            <div className="tab-content">
              <h1>Dashboard Overview</h1>
              <div className="quick-actions">
                 <div className="action-card">
                   <h3>USD Rate</h3>
                   <p>{rates['USD'] || 0} LKR</p>
                   <button onClick={() => setActiveTab('settings')}>Update Now</button>
                 </div>
                 <div className="action-card">
                   <h3>Approvals</h3>
                   <p>{pendingAgents} Pending</p>
                   <button onClick={() => setActiveTab('users')}>Review List</button>
                 </div>
                 <div className="action-card">
                   <h3>Payment Status</h3>
                   <p>{unpaidUsers} Outstanding</p>
                   <button onClick={() => setActiveTab('users')}>Track Payments</button>
                 </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="tab-content">
              <h1>User Management</h1>
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User Info</th>
                      <th>Role</th>
                      <th>License</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.uid}>
                        <td>
                          <strong>{user.fullName}</strong><br/>
                          <small>{user.email}</small>
                        </td>
                        <td><span className={`badge ${user.role}`}>{user.role}</span></td>
                        <td>{user.licenseNumber || "N/A"}</td>
                        <td>
                          <span className={`pay-status ${user.paymentStatus}`}>
                            {user.paymentStatus === 'paid' ? '✅ Paid' : '❌ Unpaid'}
                          </span>
                        </td>
                        <td><span className={`status-${user.status}`}>{user.status}</span></td>
                        <td>
                          <div className="action-group">
                            {user.role === "agent" && user.status === "pending" && (
                              <button onClick={() => handleApprove(user.uid)} className="approve-btn">Approve</button>
                            )}
                            {user.paymentStatus === "unpaid" && (
                              <button onClick={() => handleMarkAsPaid(user.uid)} className="pay-btn">Mark Paid</button>
                            )}
                            <button onClick={() => handleReject(user.uid)} className="reject-btn">Delete/Reject</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="tab-content">
              <h1>Global Settings</h1>
              <div className="settings-card">
                <h3>Multi-Currency Exchange Rates (vs LKR)</h3>
                <p className="settings-hint">Configure the LKR value for 1 unit of foreign currency.</p>
                
                <div className="admin-toolbar">
                    <button onClick={syncWithLiveApi} className="sync-btn" disabled={isUpdatingRate}>🔄 Sync Live Market</button>
                    <button onClick={clearAllRates} className="clear-btn">🧹 Clear Inputs</button>
                </div>

                <div className="rates-grid">
                  {SUPPORTED_CURRENCIES.map(curr => (
                    <div key={curr} className="rate-card">
                      <label>{curr} to LKR</label>
                      <div className="currency-input">
                        <span>Rs.</span>
                        <input 
                          type="number" 
                          value={rates[curr] || 0} 
                          onChange={(e) => setRates({ ...rates, [curr]: parseFloat(e.target.value) || 0 })} 
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <button onClick={handleUpdateRate} disabled={isUpdatingRate} className="save-settings-btn">
                  {isUpdatingRate ? "Saving..." : "💾 Save All Exchange Rates"}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;